import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import {
  BarChart3,
  Settings2,
  Package,
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  ShoppingBasket
} from 'lucide-react';

import logoDark from '/mainLogoDark.webp';
import logoBright from '/mainLogoBright.webp';
import { url } from '@/components/lessons/url';
import Title from '../lesson/Title';
import NavLink from './NavLink';
import { useAuth } from '@/hooks/useAuth';
import { useCart } from '@/hooks/useCart';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeCtx';

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { authState, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Zaktualizowane linki z ikonami Lucide
  const links = [
    { href: '/settings', title: 'Ustawienia', icon: Settings2 },
    { href: '/statistics', title: 'Statystyki', icon: BarChart3 },
    { href: '/orders', title: 'Twoje zamówienia', icon: Package }
  ];

  const handleLogout = () => {
    setDropdownVisible(false);
    logout(() => navigate('/'));
  };

  const handleNavigation = (href: string) => {
    setDropdownVisible(false);
    navigate(href);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownVisible(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <nav className='sticky top-0 z-50 flex justify-between items-center bg-neutralPrimary/80 backdrop-blur-md font-semibold py-2 px-6 border-b border-white/5'>
      {/* LEWA STRONA: LOGO */}
      <div className='flex items-center gap-6'>
        <Link to='/'>
          <img
            src={isDarkMode ? logoDark : logoBright}
            alt='Home'
            className='h-10 w-auto transition-transform hover:scale-105 active:scale-95'
          />
        </Link>
        {location.pathname === '/lesson' && (
          <Title lessonTitle={url[0].title} />
        )}
      </div>

      {/* PRAWA STRONA: NAV & USER */}
      <div className='flex items-center gap-2'>
        <NavLink
          href='/courses'
          title='Kursy'
          className='text-textPrimary hover:text-primary transition-colors duration-200 px-5'
        />

        {authState?.user ? (
          <>
            <div className='relative' ref={dropdownRef}>
              <button
                className={`flex items-center gap-3 py-2 pl-1.5 pr-3 rounded-lg transition-all duration-300 ${
                  dropdownVisible
                    ? 'bg-neutralSecondary ring-1 ring-white/10'
                    : 'hover:bg-neutralSecondary'
                }`}
                onClick={() => setDropdownVisible(!dropdownVisible)}
              >
                <Avatar
                  alt='User'
                  src={authState?.user.avatarUrl}
                  sx={{ width: 32, height: 32, border: '2px solid rgba(255,255,255,0.05)' }}
                />
                <span className='text-sm text-textPrimary hidden md:inline-block font-bold tracking-tight'>
                  {authState?.user.displayName}
                </span>
                <ChevronDown
                  size={14}
                  className={`text-textPrimary opacity-40 transition-transform duration-300 ${dropdownVisible ? 'rotate-180' : ''}`}
                />
              </button>

              {/* PANEL DROPDOWNA */}
              {dropdownVisible && (
                <div className='absolute right-0 mt-4 w-64 bg-neutralPrimary border border-white/10 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-50 overflow-hidden animate-in fade-in slide-in-from-top-3 duration-300'>

                  {/* Nagłówek Dropdowna */}
                  <div className='px-6 py-5 bg-neutralSecondary/50 border-b border-white/5'>
                    <p className='text-[10px] font-black uppercase tracking-[0.15em] text-primary mb-1'>Zalogowany jako</p>
                    <p className='text-sm font-bold text-textPrimary truncate'>{authState.user.email}</p>
                  </div>

                  <ul className='p-2'>
                    {links.map((link) => (
                      <li key={link.href}>
                        <button
                          onClick={() => handleNavigation(link.href)}
                          className='w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-neutralSecondary text-sm text-textPrimary transition-all group text-left'
                        >
                          <div className='p-2 rounded-xl bg-neutralSecondary/50 group-hover:bg-primary/10 group-hover:text-primary transition-colors'>
                            <link.icon size={18} />
                          </div>
                          <span className='font-semibold'>{link.title}</span>
                        </button>
                      </li>
                    ))}

                    <div className='my-2 border-t border-white/5 mx-4' />

                    <li>
                      <button
                        onClick={handleLogout}
                        className='w-full flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-red-500/5 text-sm text-red-500 transition-all group text-left'
                      >
                        <div className='p-2 rounded-xl bg-red-500/10 group-hover:bg-red-500 group-hover:text-white transition-all'>
                          <LogOut size={18} />
                        </div>
                        <span className='font-semibold'>Wyloguj się</span>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* KOSZYK */}
            {itemCount > 0 && (
              <Link to='/cart' className='relative ml-2'>
                <div className='flex items-center justify-center h-11 w-11 rounded-full bg-primary/10 hover:bg-primary transition-all duration-300 group'>
                  <ShoppingBasket className='text-primary group-hover:text-white transition-colors' size={20} />
                  <span className='absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full ring-4 ring-neutralPrimary'>
                    {itemCount}
                  </span>
                </div>
              </Link>
            )}
          </>
        ) : (
          <div className='flex items-center gap-2'>
            <Button
              variant="ghost"
              onClick={toggleTheme}
              className="relative rounded-full h-10 w-10 p-0 mr-2 text-textPrimary hover:bg-primary/10 hover:text-primary transition-all duration-300 hover:scale-110 active:scale-90 overflow-hidden group"
            >
              <Sun
                size={18}
                className={`transition-all duration-500 transform ${
                  isDarkMode ? 'rotate-90 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'
                }`}
              />

              <Moon
                size={18}
                className={`absolute transition-all duration-500 transform ${
                  isDarkMode ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-0 opacity-0'
                }`}
              />

              {/* Opcjonalny efekt poświaty przy hoverze */}
              <span className="absolute inset-0 rounded-full bg-primary/0 group-hover:bg-primary/5 transition-colors duration-300" />
            </Button>
            <Button asChild variant={"orange"} className="px-6 font-bold h-10">
              <Link to='/user-actions/login'>Zaloguj się</Link>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}